import { useState, useEffect } from 'react';
import { Users, UserPlus, Edit2, Trash2, X, Send, Copy, Mail, Save, Shield, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from './Toast.jsx';
import {
  sendTeamInvite as apiSendTeamInvite,
  listTeamInvitations as apiListTeamInvitations,
  revokeTeamInvitation as apiRevokeTeamInvitation,
  resendTeamInvitation as apiResendTeamInvitation,
  updateAssistantSupervisor as apiUpdateAssistantSupervisor,
  updateMemberRole as apiUpdateMemberRole,
  updateSeatProfile as apiUpdateSeatProfile,
  deleteTeamMember as apiDeleteTeamMember,
  comprarAsiento as apiComprarAsiento,
} from '../api.js';
import { SEAT_PERMISSION_GROUPS, normalizeSeatPermissions } from '../seatPermissions.js';
import { nombreDelRol, esCuentaDeDosAgentes } from '../rolesDeLaCuenta.js';
import { SOLO_SUS_PROSPECTOS, esSoloSusProspectos, valorDelDesplegable } from '../alcanceDelAsiento.js';

const uid = () => Math.random().toString(36).substr(2, 9);

// POR QUÉ SE RECHAZÓ UNA INVITACIÓN, dicho para quien la mandó.
//
// La API contesta en inglés y con un `code`; la traducción vive aquí porque es
// la pantalla la que sabe en qué idioma está esta persona. Comparar el TEXTO
// del error sería atarse a una frase que cualquiera puede reescribir.
const MOTIVOS_INVITACION = {
  CANNOT_INVITE_SELF: {
    es: "Ese es TU correo. Invitarte a ti misma convertiría tu cuenta de dueña en un asiento. Escribe el correo de la otra persona.",
    en: "That is YOUR email. Inviting yourself would turn your owner account into a team member. Use the other person's email.",
  },
  ALREADY_IN_TEAM: {
    es: "Esa persona ya está en tu equipo. Para cambiarle el rol o los permisos usa su tarjeta aquí mismo, no una invitación nueva.",
    en: "That person is already on your team. To change their role or permissions use their card here, not a new invitation.",
  },
  EMAIL_OWNS_ANOTHER_ACCOUNT: {
    es: "Ese correo ya es el de la dueña de otra cuenta de Deskte, así que no puede entrar aquí como agente ni como asiento. Pídele otro correo o escribe a soporte@deskte.com.",
    en: "That email already owns another Deskte account, so it cannot join here as an agent or seat. Ask them for a different email, or write to soporte@deskte.com.",
  },
};

export function UsersManager({ users, onUsersChange, currentUser, t, lang }) {
  const es = lang === "es";
  // NAVEGANDO COMO AGENCIA: la suplantacion cambia la AGENCIA, no el usuario.
  // `currentUser` sigue siendo la duenna de la plataforma (rol mapeado a
  // `solo_agent`), asi que esta pantalla creia que la Platform Admin era la
  // agente Solo de la agencia visitada: pintaba "No tienes asientos aun"
  // (buscaba SUS asientos, y no tiene ninguno ahi), ofrecia "Hacer Admin"
  // sobre la titular de verdad, dejaba los asientos sin botones (son de otra
  // persona) y encima daba papelera sobre la tarjeta de la duenna
  // (Lissette, 2026-09-10, agencia de Maria Soraya).
  //
  // En el backend PLATFORM_OWNER ya se salta `requireRole`
  // (middleware/rbac.middleware.ts), asi que la pantalla debe tratarla como
  // duenna de la agencia que esta visitando — nunca como agente ni asiento.
  const esPlatformOwner = !!currentUser?.isPlatformOwner;
  const isAgencyOwner = esPlatformOwner || currentUser?.role === "agency_owner";
  const isAgent = !esPlatformOwner && ["agent","solo_agent"].includes(currentUser?.role);
  const isAssistant = !esPlatformOwner && currentUser?.role === "assistant";
  const COLORS = ["#1d6fa4","#6366f1","#10b981","#f59e0b","#ef4444","#8b5cf6","#ec4899","#14b8a6","#f97316"];
  const roleLabels = { agency_owner: es ? "Admin de agencia" : "Agency Admin", agency_admin: "Admin", agent: es ? "Agente" : "Agent", solo_agent: es ? "Agente Solo" : "Solo Agent", assistant: es ? "Asiento" : "Seat" };
  const roleIcons = { agency_owner: "👑", agency_admin: "🔧", agent: "👤", solo_agent: "🛡️", assistant: "💺" };

  const [modal, setModal] = useState(null); // null | "add" | "invite" | user object (edit)
  const [form, setForm] = useState({ name: "", email: "", role: "assistant", pin: "", color: COLORS[1], parentAgentId: currentUser?.id || "", seatLabel: "" });
  const [confirmPin, setConfirmPin] = useState("");
  const [pinErr, setPinErr] = useState("");
  const [delConfirm, setDelConfirm] = useState(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("assistant");
  const [inviteSent, setInviteSent] = useState(null);
  const [inviteParent, setInviteParent] = useState(currentUser?.id || "");
  const [expandedPerms, setExpandedPerms] = useState({}); // { [userId]: string[] } expanded group ids
  const [roleConfirm, setRoleConfirm] = useState(null); // { id, to } co-owner promote/demote confirmation
  // "Private books" agency mode (owners + agency-seats see only the owners'
  // pool; agents' books stay private even from owners). READ-ONLY here: the
  // switch is PLATFORM-owner-only (Deskte configures it per agency) so an
  // agency owner can never flip the wall off and reveal agents' clients.
  const booksPrivate = !!currentUser?.agency?.agentBooksPrivate;

  // Sidebar click on "Team" closes any open form / confirm modal.
  useEffect(() => {
    const h = (e) => { if (e?.detail?.view === 'team') { setModal(null); setDelConfirm(null); setInviteSent(null); } };
    window.addEventListener('deskte:nav-reset', h);
    return () => window.removeEventListener('deskte:nav-reset', h);
  }, []);

  // Agents in the team (for assigning assistants)
  const agents = users.filter(u => ["agent","solo_agent","agency_owner","agency_admin"].includes(u.role));
  // My assistants (for agents view)
  const myAssistants = users.filter(u => u.role === "assistant" && u.parentAgentId === currentUser?.id);

  // What can current user add?
  const canAddAgent = isAgencyOwner;
  const canAddAssistant = isAgencyOwner || isAgent;
  const canAdd = canAddAgent || canAddAssistant;

  // Solo "add one admin" add-on. A Solo agent may add ONE full-access admin:
  // a teammate is invited as an AGENT, then promoted to a co-owner
  // (agency_owner) — the only role besides the solo agent with full visibility.
  // The slot is "used" once an admin exists OR a future-admin agent has already
  // joined and is awaiting promotion. Priced at $40/mo ($30 for founders),
  // shown here; not billed during the beta. See team.routes PATCH /members/:id/role.
  const isSolo = !esPlatformOwner && currentUser?.role === "solo_agent";
  const isFounderAcct = !!currentUser?.agency?.isFounder;
  const soloActiveAdmin = isSolo ? users.find(u => u.role === "agency_owner") : null;
  const soloPendingAdmin = isSolo ? users.find(u => u.role === "agent" && u.id !== currentUser?.id) : null;
  const soloAdminSlotUsed = !!(soloActiveAdmin || soloPendingAdmin);
  const ADMIN_PRICE = isFounderAcct ? "$30" : "$40";
  const adminPriceLine = es ? `${ADMIN_PRICE}/mes · $30 para founders` : `${ADMIN_PRICE}/mo · $30 for founders`;

  // Which users does current user see?
  // In API mode the backend already filters /team/members by agencyId, so showing
  // all returned users is correct. Local-only mode keeps the original role-based
  // visibility for the demo data.
  const isApiModeUMv = typeof window !== "undefined" && !!window.__deskteAuth;
  const visibleUsers = isApiModeUMv
    ? users
    : isAgencyOwner
      ? users
      : isAgent
        ? [currentUser, ...myAssistants].filter(Boolean)
        : [currentUser].filter(Boolean);

  // UN ASIENTO SE PINTA DEBAJO DE QUIEN LO SUPERVISA, sea agente, admin o
  // duenna. Antes la lista solo recorria a los AGENTES para colgarles sus
  // asistentes, y mandaba a "Sin Asignar" unicamente a los que no tenian
  // supervisor. Un asiento supervisado por la duenna no entraba en ninguna de
  // las dos ramas y NO SE PINTABA (Lissette, 2026-09-10: la ficha decia "2
  // asientos" y la lista no ensennaba ninguno). Los contadores si los contaban,
  // asi que el numero y la lista se contradecian en la misma pantalla.
  //
  // Pasa en cuanto la principal de la cuenta es `agency_owner` en vez de
  // `agent`/`solo_agent` — la forma que deja una agencia que se paso de Agency
  // a Solo, que es justo lo que se estuvo viendo toda esta semana.
  const asientosDe = (id) => visibleUsers.filter(a => a.role === "assistant" && a.parentAgentId === id);
  // Quien PUEDE aparecer con ficha propia arriba. Si el supervisor de un
  // asiento no esta aqui, ese asiento cae en "Sin Asignar" en vez de perderse:
  // preguntar por "no tiene supervisor" dejaba fuera a los que si tenian uno
  // que la lista no pintaba.
  const idsConFichaPropia = new Set(visibleUsers.filter(u => u.role !== "assistant").map(u => u.id));
  const asientosHuerfanos = visibleUsers.filter(
    (u) => u.role === "assistant" && !idsConFichaPropia.has(u.parentAgentId),
  );

  // SUPERVISOR POR DEFECTO DE UN ASIENTO NUEVO. Normalmente eres tu misma,
  // pero una Platform Admin navegando como agencia NO esta en esa agencia: su
  // id no sale en el desplegable, el navegador ensennaba el primer agente y se
  // mandaba el id de ella. Cae al primer productor de la lista.
  const supervisorPorDefecto = () => {
    const yo = currentUser?.id || "";
    if (yo && agents.some(a => a.id === yo)) return yo;
    return agents[0]?.id || yo;
  };

  const openAdd = () => {
    const defaultRole = canAddAgent ? "agent" : "assistant";
    setForm({ name: "", email: "", role: defaultRole, pin: "", color: COLORS[Math.floor(Math.random() * COLORS.length)], parentAgentId: supervisorPorDefecto(), seatLabel: "" });
    setConfirmPin(""); setPinErr(""); setModal("add");
  };

  const openEdit = (u) => {
    setForm({ ...u, seatLabel: u.seatLabel || "" }); setConfirmPin(u.pin || ""); setPinErr(""); setModal(u);
  };

  const openInvite = () => {
    setInviteEmail(""); setInviteRole(canAddAgent ? "agent" : "assistant"); setInviteParent(supervisorPorDefecto()); setInviteSent(null); setModal("invite");
  };

  // Solo: invite the future admin. They join as an AGENT (the only role the
  // accept flow allows below owner), then the solo agent promotes them to
  // full-access admin with one click. Pre-selects the "agent" invite role.
  const openInviteAdmin = () => {
    setInviteEmail(""); setInviteRole("agent"); setInviteParent(supervisorPorDefecto()); setInviteSent(null); setModal("invite");
  };

  const save = async () => {
    if (!form.name.trim()) return;
    if (form.pin.length !== 4) { setPinErr(es ? "PIN debe ser 4 dígitos" : "PIN must be 4 digits"); return; }
    if (form.pin !== confirmPin) { setPinErr(es ? "PINs no coinciden" : "PINs do not match"); return; }
    const initials = form.name.trim().split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2);
    const parentId = form.role === "assistant" ? (form.parentAgentId || currentUser?.id) : null;
    const seatLabel = form.role === "assistant" ? (form.seatLabel || "").trim() : "";
    if (modal === "add") {
      onUsersChange(prev => [...prev, { ...form, id: uid(), initials, parentAgentId: parentId, agencyId: currentUser?.agencyId || null, seatLabel: seatLabel || null }]);
    } else {
      onUsersChange(prev => prev.map(u => u.id === modal.id ? { ...form, id: modal.id, initials, parentAgentId: parentId, seatLabel: seatLabel || null } : u));
      // Persist seatLabel to backend in API mode (only meaningful for seats).
      if (isApiModeUM && modal.role === "assistant" && (modal.seatLabel || "") !== seatLabel) {
        try {
          await apiUpdateSeatProfile(modal.id, { seatLabel: seatLabel || null });
        } catch (err) {
          toast.error((es ? "Error guardando nombre del asiento: " : "Error saving seat name: ") + err.message);
        }
      }
    }
    setModal(null);
  };

  const isApiModeUM = typeof window !== "undefined" && !!window.__deskteAuth;
  const [pendingInvites, setPendingInvites] = useState([]);
  const [inviteBusy, setInviteBusy] = useState(false);
  // SE ACABARON LOS ASIENTOS. En vez de un aviso rojo y punto muerto, se
  // ofrece la salida con su precio delante. Guarda el correo que se estaba
  // invitando para poder reintentar solo, sin hacerla escribirlo otra vez.
  const [faltaAsiento, setFaltaAsiento] = useState(null); // null | { limite }
  const [comprando, setComprando] = useState(false);
  // For a Solo account, a pending AGENT invitation IS a pending admin invite
  // (the only kind a solo sends). Count it toward the one-admin slot so we
  // don't offer "Add admin" twice while one is still in flight.
  const soloHasPendingAdminInvite = isSolo && pendingInvites.some(i => String(i.role).toUpperCase() === "AGENT");
  const soloAdminSlotTaken = soloAdminSlotUsed || soloHasPendingAdminInvite;

  const refreshInvites = async () => {
    if (!isApiModeUM) return;
    try {
      const { invitations } = await apiListTeamInvitations();
      setPendingInvites(invitations);
    } catch (err) {
      // owners only — ignore for non-owners
    }
  };

  useEffect(() => { refreshInvites(); }, [isApiModeUM]);

  const sendInvite = async () => {
    if (!inviteEmail.trim()) return;
    if (isApiModeUM) {
      // TU PROPIO CORREO NO (E-309, 2026-09-21).
      //
      // La agencia de Luisa mandó una invitación de asiento a su propio correo.
      // Al abrir el enlace, el servidor no creó un asiento: convirtió SU cuenta
      // de dueña en asiento, supervisada por ella misma, y su agencia se quedó
      // sin nadie que mande. El servidor ya lo rechaza —y ahí es donde cuenta,
      // porque esto es solo una pantalla—, pero decirlo aquí le ahorra el viaje
      // y explica el error en su idioma en vez de soltar el texto de la API.
      if (currentUser?.email && inviteEmail.trim().toLowerCase() === String(currentUser.email).toLowerCase()) {
        toast.error(es
          ? "Ese es TU correo. Invitarte a ti misma convertiría tu cuenta de dueña en un asiento. Escribe el correo de la otra persona."
          : "That is YOUR email. Inviting yourself would turn your owner account into a team member. Use the other person's email.");
        return;
      }
      setInviteBusy(true);
      try {
        // Solo: an "agent" invite IS the admin add-on — mark it so the backend
        // auto-promotes them to a full-access admin when they accept (capped at 1).
        const result = await apiSendTeamInvite({ email: inviteEmail.trim(), role: inviteRole, promoteToAdmin: isSolo && inviteRole === "agent" });
        setInviteSent({ email: result.email, role: result.role, link: result.inviteUrl, emailSent: result.emailSent });
        await refreshInvites();
      } catch (err) {
        if (err.code === "SEAT_LIMIT_REACHED") {
          setFaltaAsiento({ limite: err.limite, enPrueba: err.enPrueba === true });
        } else if (MOTIVOS_INVITACION[err.code]) {
          // El servidor manda un `code`; el texto en español vive aquí, no en
          // la API. Así la explicación se lee igual de bien en las dos puntas.
          toast.error(MOTIVOS_INVITACION[err.code][es ? "es" : "en"]);
        } else {
          toast.error((es ? "No se pudo enviar la invitación: " : "Failed to send invitation: ") + err.message);
        }
      } finally {
        setInviteBusy(false);
      }
      return;
    }
    const inviteLink = `https://deskte.com/invite/${uid()}`;
    setInviteSent({ email: inviteEmail, role: inviteRole, link: inviteLink });
  };

  // Compra el asiento y REINTENTA la invitación sola. Que la persona tenga que
  // volver a pulsar "Invitar" después de pagar es hacerle repetir un paso que
  // ya hizo.
  const comprarYReintentar = async () => {
    setComprando(true);
    try {
      const r = await apiComprarAsiento();
      setFaltaAsiento(null);
      // Se dice lo que PASO, no lo que se pidio: en prueba no se cobro nada y
      // callarlo dejaria a la persona esperando un cargo que no llega — o peor,
      // creyendo que el asiento fue gratis.
      toast.success(
        r.enPrueba
          ? (es
            ? `Asiento agregado. Ahora puedes tener ${r.maxAssistants}. No se cobro nada: se suma a tu plan cuando lo actives.`
            : `Seat added. You can now have ${r.maxAssistants}. Nothing was charged: it joins your plan when you activate it.`)
          : (es
            ? `Asiento agregado. Ahora puedes tener ${r.maxAssistants}.`
            : `Seat added. You can now have ${r.maxAssistants}.`));
      await sendInvite();
    } catch (e) {
      toast.error((es ? "No se pudo agregar el asiento: " : "Could not add the seat: ") + e.message);
    } finally {
      setComprando(false);
    }
  };

  const revokeInvite = async (id) => {
    if (!isApiModeUM) return;
    try {
      await apiRevokeTeamInvitation(id);
      await refreshInvites();
    } catch (err) {
      toast.error((es ? "Error: " : "Error: ") + err.message);
    }
  };

  const resendInvite = async (id) => {
    if (!isApiModeUM) return;
    try {
      const result = await apiResendTeamInvitation(id);
      if (result.emailSent) {
        toast.success(es ? "Email reenviado." : "Email re-sent.");
      } else {
        toast.warn(es ? "Email no enviado (SMTP no configurado). Comparte el link manualmente." : "Email not sent (SMTP not configured). Share the link manually.", { link: result.inviteUrl });
      }
    } catch (err) {
      toast.error((es ? "Error: " : "Error: ") + err.message);
    }
  };

  // Render a user card
  // Todo lo que la tarjeta necesita del padre, en un solo prop. Es un VALOR,
  // no el tipo del componente: puede cambiar en cada render sin remontar nada.
  const ctx = {
    users, onUsersChange, currentUser, lang, es, isAgencyOwner, isAgent,
   isSolo, roleIcons, openEdit, setDelConfirm, expandedPerms,
   setExpandedPerms, roleConfirm, setRoleConfirm, booksPrivate, agents,
   soloActiveAdmin, adminPriceLine,
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1.2rem" }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: ".5rem" }}>
            <Users size={20} style={{ color: "var(--c-cyan,#00d4ff)" }} />
            <h2 style={{ fontWeight: 800, fontSize: "1.1rem", color: "var(--c-white,#eef6ff)", margin: 0 }}>{es ? "Mi Equipo" : "My Team"}</h2>
          </div>
          <p style={{ fontSize: ".72rem", color: "var(--c-text3,#45688a)", margin: ".25rem 0 0" }}>
            {isAgencyOwner
              ? (es ? "Administra agentes y asientos de tu agencia" : "Manage your agency's agents and seats")
              : isSolo
                ? (es ? "Administra tus asientos y tu admin" : "Manage your seats and your admin")
                : isAgent
                  ? (es ? "Administra tus asientos" : "Manage your seats")
                  : (es ? "Tu perfil de equipo" : "Your team profile")}
          </p>
        </div>
        {canAdd && (
          <div style={{ display: "flex", gap: ".35rem" }}>
            <button type="button" onClick={openInvite} style={{ display: "flex", alignItems: "center", gap: 5, background: "var(--c-surface2,#112538)", border: "1px solid var(--c-border,#1a3550)", color: "var(--c-cyan,#00d4ff)", fontSize: ".75rem", fontWeight: 700, padding: ".45rem .85rem", borderRadius: 9, cursor: "pointer", fontFamily: "inherit" }}>
              <Mail size={14} />{es ? "Invitar" : "Invite"}
            </button>
            {/* "Agregar" is local-only (creates a phantom user that never persists
                and can't log in). In API/production mode the real path is "Invitar",
                so hide Add there to avoid the confusing vanishing-teammate trap. */}
            {!isApiModeUM && (
              <button type="button" onClick={openAdd} style={{ display: "flex", alignItems: "center", gap: 5, background: "linear-gradient(135deg,#059669,#10b981)", border: "none", color: "#fff", fontSize: ".75rem", fontWeight: 700, padding: ".45rem .85rem", borderRadius: 9, cursor: "pointer", fontFamily: "inherit" }}>
                <UserPlus size={14} />{es ? "Agregar" : "Add"}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Solo "add one admin" add-on. A Solo agent can add ONE full-access
          admin: invite them (they join as an agent), then promote with one
          click. Priced at $40/mo ($30 founders) — shown here, not billed
          during the beta. */}
      {isSolo && (
        <div style={{ background: "rgba(251,191,36,.06)", border: "1px solid rgba(251,191,36,.3)", borderRadius: 12, padding: ".7rem .85rem", marginBottom: "1rem", display: "flex", alignItems: "flex-start", gap: ".55rem", flexWrap: "wrap" }}>
          <span style={{ fontSize: "1.05rem", lineHeight: 1, flexShrink: 0 }}>👑</span>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontWeight: 700, fontSize: ".82rem", color: "var(--c-white,#eef6ff)" }}>
              {es ? "Admin con acceso total" : "Full-access Admin"}
              <span style={{ marginLeft: ".4rem", fontSize: ".64rem", fontWeight: 800, color: "#fbbf24", background: "rgba(251,191,36,.12)", borderRadius: 100, padding: "2px 8px" }}>{adminPriceLine}</span>
            </div>
            {soloActiveAdmin ? (
              <p style={{ fontSize: ".68rem", color: "var(--c-text2,#7aa3c0)", margin: ".25rem 0 0", lineHeight: 1.45 }}>
                ✅ {es ? "Admin activo:" : "Active admin:"} <b style={{ color: "var(--c-white,#eef6ff)" }}>{soloActiveAdmin.name}</b>. {es ? "Ve y maneja TODO tu CRM. Puedes quitarlo desde su tarjeta. (Máximo 1 admin.)" : "Sees and manages your ENTIRE CRM. Remove from their card anytime. (Max 1 admin.)"}
              </p>
            ) : soloHasPendingAdminInvite ? (
              <p style={{ fontSize: ".68rem", color: "var(--c-text2,#7aa3c0)", margin: ".25rem 0 0", lineHeight: 1.45 }}>
                ⏳ {es ? "Invitación de admin enviada. En cuanto acepte, quedará admin con acceso total automáticamente. (Máximo 1 admin.)" : "Admin invite sent. As soon as they accept, they'll automatically become a full-access admin. (Max 1 admin.)"}
              </p>
            ) : soloPendingAdmin ? (
              <p style={{ fontSize: ".68rem", color: "var(--c-text2,#7aa3c0)", margin: ".25rem 0 0", lineHeight: 1.45 }}>
                ⏳ <b style={{ color: "var(--c-white,#eef6ff)" }}>{soloPendingAdmin.name}</b> {es ? "ya entró como agente. Pulsa “👑 Hacer Admin” en su tarjeta para darle acceso total." : "joined as an agent. Click “👑 Make Admin” on their card to grant full access."}
              </p>
            ) : (
              <p style={{ fontSize: ".68rem", color: "var(--c-text2,#7aa3c0)", margin: ".25rem 0 0", lineHeight: 1.45 }}>
                {es ? "Agrega 1 admin que vea y maneje todo tu CRM (clientes, pólizas, reportes). Queda admin automáticamente al aceptar. No se cobra durante la beta." : "Add 1 admin who sees and manages your entire CRM (clients, policies, reports). They become admin automatically on accept. Not billed during the beta."}
              </p>
            )}
          </div>
          {!soloAdminSlotTaken && (
            <button type="button" onClick={openInviteAdmin} style={{ display: "flex", alignItems: "center", gap: 5, alignSelf: "center", background: "linear-gradient(135deg,#f59e0b,#d97706)", border: "none", color: "#fff", fontSize: ".72rem", fontWeight: 800, padding: ".45rem .85rem", borderRadius: 9, cursor: "pointer", fontFamily: "inherit", flexShrink: 0 }}>
              <UserPlus size={14} />{es ? "Agregar Admin" : "Add Admin"}
            </button>
          )}
        </div>
      )}

      {/* Private-books mode notice — READ-ONLY. The switch is platform-owner
          only (Deskte), so the owner sees it's active but cannot turn it off. */}
      {isAgencyOwner && booksPrivate && (
        <div style={{ background: "rgba(0,212,255,.07)", border: "1px solid rgba(0,212,255,.3)", borderRadius: 12, padding: ".7rem .85rem", marginBottom: "1rem", display: "flex", alignItems: "flex-start", gap: ".55rem" }}>
          <Shield size={15} style={{ color: "var(--c-cyan,#00d4ff)", flexShrink: 0, marginTop: 1 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: ".78rem", color: "var(--c-white,#eef6ff)" }}>
              🔒 {es ? "Carteras de agentes privadas: activado" : "Private agent books: on"}
            </div>
            <p style={{ fontSize: ".68rem", color: "var(--c-text3,#45688a)", margin: ".25rem 0 0", lineHeight: 1.45 }}>
              {es
                ? "Cada agente maneja su propia cartera de clientes de forma privada. Como dueño ves solo el grupo de clientes de los dueños y los reportes de producción de cada agente (no sus clientes)."
                : "Each agent manages their own client book privately. As an owner you see only the owners' client pool plus each agent's production reports (not their clients)."}
            </p>
          </div>
        </div>
      )}

      {/* Stats bar */}
      <div style={{ display: "flex", gap: ".5rem", marginBottom: "1rem", flexWrap: "wrap" }}>
        {[
          { label: es ? "Total" : "Total", value: visibleUsers.length, color: "var(--c-cyan,#00d4ff)" },
          { label: es ? "Agentes" : "Agents", value: visibleUsers.filter(u => ["agent","solo_agent"].includes(u.role)).length, color: "var(--c-green,#00ff88)" },
          { label: es ? "Asientos" : "Seats", value: visibleUsers.filter(u => u.role === "assistant").length, color: "#818cf8" },
        ].map((s, i) => (
          <div key={i} style={{ flex: 1, minWidth: 90, background: "var(--c-surface2,#112538)", border: "1px solid var(--c-border,#1a3550)", borderRadius: 10, padding: ".5rem .65rem", textAlign: "center" }}>
            <div style={{ fontSize: "1.1rem", fontWeight: 800, color: s.color }}>{s.value}</div>
            <div style={{ fontSize: ".6rem", color: "var(--c-text3,#45688a)", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em" }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Pending invitations */}
      {isApiModeUM && pendingInvites.length > 0 && (
        <div style={{ background: "var(--c-surface2,#112538)", border: "1px solid var(--c-border,#1a3550)", borderRadius: 12, padding: ".75rem .85rem", marginBottom: "1rem" }}>
          <div style={{ fontSize: ".7rem", fontWeight: 700, color: "var(--c-text2,#7aa3c0)", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: ".5rem" }}>
            ✉️ {es ? "Invitaciones Pendientes" : "Pending Invitations"} ({pendingInvites.length})
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: ".35rem" }}>
            {pendingInvites.map(inv => (
              <div key={inv.id} style={{ display: "flex", alignItems: "center", gap: ".5rem", background: "var(--c-surface,#0d1c2e)", border: "1px solid var(--c-border,#1a3550)", borderRadius: 9, padding: ".4rem .65rem" }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: ".78rem", fontWeight: 600, color: "var(--c-white,#eef6ff)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{inv.email}</div>
                  <div style={{ fontSize: ".6rem", color: "var(--c-text3,#45688a)" }}>
                    {String(inv.role).toLowerCase().replace("_", " ")} · {es ? "expira " : "expires "}{new Date(inv.expiresAt).toLocaleDateString("en-US")}
                  </div>
                </div>
                <button type="button" onClick={() => { navigator.clipboard.writeText(inv.inviteUrl).catch(() => {}); toast.success(es ? "¡Copiado!" : "Copied!"); }} title={es ? "Copiar link" : "Copy link"} style={{ background: "var(--c-surface3,#162d44)", border: "1px solid var(--c-border,#1a3550)", color: "var(--c-cyan,#00d4ff)", cursor: "pointer", padding: "5px 7px", borderRadius: 7, lineHeight: 0 }}><Copy size={12} /></button>
                <button type="button" onClick={() => resendInvite(inv.id)} title={es ? "Reenviar email" : "Resend email"} style={{ background: "var(--c-surface3,#162d44)", border: "1px solid var(--c-border,#1a3550)", color: "var(--c-text2,#7aa3c0)", cursor: "pointer", padding: "5px 7px", borderRadius: 7, lineHeight: 0 }}><Send size={12} /></button>
                <button type="button" onClick={() => revokeInvite(inv.id)} title={es ? "Revocar" : "Revoke"} style={{ background: "var(--c-surface3,#162d44)", border: "1px solid var(--c-border,#1a3550)", color: "#ef4444", cursor: "pointer", padding: "5px 7px", borderRadius: 7, lineHeight: 0 }}><Trash2 size={12} /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* User list — grouped by agent with their assistants underneath */}
      <div style={{ display: "flex", flexDirection: "column", gap: ".4rem" }}>
        {isAgencyOwner ? (
          <>
            {/* Cada persona con ficha propia lleva SUS asientos debajo: la
                duenna y los admins tambien supervisan, no solo los agentes. */}
            {visibleUsers
              .filter(u => ["agency_owner", "agency_admin", "agent", "solo_agent"].includes(u.role))
              .map(persona => (
                <div key={persona.id}>
                  <UserCard u={persona} ctx={ctx} />
                  {asientosDe(persona.id).map(ast => (
                    <div key={ast.id} style={{ marginTop: ".25rem" }}><UserCard u={ast} indent ctx={ctx} /></div>
                  ))}
                </div>
              ))}
            {/* Sin asignar: los que no cuelgan de NADIE de los de arriba. */}
            {asientosHuerfanos.length > 0 && (
              <>
                <div style={{ fontSize: ".62rem", color: "var(--c-text3,#45688a)", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", marginTop: ".5rem", paddingLeft: ".3rem" }}>{es ? "Sin Asignar" : "Unassigned"}</div>
                {asientosHuerfanos.map(u => <UserCard key={u.id} u={u} ctx={ctx} />)}
              </>
            )}
          </>
        ) : (
          <>
            {/* Agent sees themselves + their assistants */}
            {visibleUsers.map(u => <UserCard key={u.id} u={u} ctx={ctx} />)}
            {isAgent && myAssistants.length === 0 && (
              <div style={{ textAlign: "center", padding: "1.5rem", background: "var(--c-surface2,#112538)", borderRadius: 12, border: "1px dashed var(--c-border,#1a3550)" }}>
                <UserPlus size={24} style={{ color: "var(--c-text3,#45688a)", margin: "0 auto .5rem" }} />
                <p style={{ fontSize: ".78rem", color: "var(--c-text3,#45688a)", fontWeight: 600, margin: 0 }}>{es ? "No tienes asientos aún" : "You don't have any seats yet"}</p>
                <p style={{ fontSize: ".68rem", color: "var(--c-text3,#45688a)", margin: ".3rem 0 .75rem", opacity: .7 }}>{es ? "Invita un asiento para delegar tareas" : "Invite a seat to delegate tasks"}</p>
                {/* In API mode "Agregar" doesn't create a real login — point the
                    empty-state CTA at the working Invite flow instead. */}
                <button type="button" onClick={isApiModeUM ? openInvite : openAdd} style={{ display: "inline-flex", alignItems: "center", gap: 5, background: "linear-gradient(135deg,#059669,#10b981)", border: "none", color: "#fff", fontSize: ".75rem", fontWeight: 700, padding: ".45rem 1rem", borderRadius: 9, cursor: "pointer", fontFamily: "inherit" }}>
                  <UserPlus size={14} />{isApiModeUM ? (es ? "Invitar Asiento" : "Invite Seat") : (es ? "Agregar Asiento" : "Add Seat")}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* ADD USER MODAL */}
      {modal === "add" && (
        <div style={{ position: "fixed", inset: 0, zIndex: 9999, background: "rgba(0,0,0,.85)", display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" }}>
          <div style={{ background: "var(--c-surface,#0d1c2e)", border: "1px solid var(--c-border,#1a3550)", borderRadius: 14, padding: "1.5rem", maxWidth: 440, width: "100%" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
              <div style={{ fontWeight: 800, fontSize: "1rem", color: "var(--c-white,#eef6ff)" }}>{es ? "Agregar Miembro" : "Add Team Member"}</div>
              <button type="button" onClick={() => setModal(null)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--c-text3,#45688a)" }}><X size={18} /></button>
            </div>

            {/* Role selector */}
            <div style={{ marginBottom: ".65rem" }}>
              <label style={{ fontSize: ".62rem", color: "var(--c-text3,#45688a)", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", display: "block", marginBottom: 4 }}>{es ? "Rol" : "Role"}</label>
              <div style={{ display: "flex", gap: ".3rem" }}>
                {canAddAgent && (
                  <button type="button" onClick={() => setForm(f => ({ ...f, role: "agent", parentAgentId: "" }))}
                    style={{ flex: 1, padding: ".45rem", borderRadius: 8, border: `1px solid ${form.role === "agent" ? "rgba(0,255,136,.4)" : "var(--c-border,#1a3550)"}`, background: form.role === "agent" ? "rgba(0,255,136,.08)" : "transparent", color: form.role === "agent" ? "var(--c-green,#00ff88)" : "var(--c-text3,#45688a)", fontSize: ".72rem", fontWeight: 700, cursor: "pointer", fontFamily: "inherit", textAlign: "center" }}>
                    <div>👤 {es ? "Agente" : "Agent"}</div>
                  </button>
                )}
                <button type="button" onClick={() => setForm(f => ({ ...f, role: "assistant", parentAgentId: currentUser?.id || "" }))}
                  style={{ flex: 1, padding: ".45rem", borderRadius: 8, border: `1px solid ${form.role === "assistant" ? "rgba(129,140,248,.4)" : "var(--c-border,#1a3550)"}`, background: form.role === "assistant" ? "rgba(129,140,248,.08)" : "transparent", color: form.role === "assistant" ? "#818cf8" : "var(--c-text3,#45688a)", fontSize: ".72rem", fontWeight: 700, cursor: "pointer", fontFamily: "inherit", textAlign: "center" }}>
                  <div>💺 {es ? "Asiento" : "Seat"}</div>
                </button>
                {isAgencyOwner && (
                  <button type="button" onClick={() => setForm(f => ({ ...f, role: "agency_admin", parentAgentId: "" }))}
                    style={{ flex: 1, padding: ".45rem", borderRadius: 8, border: `1px solid ${form.role === "agency_admin" ? "rgba(251,191,36,.4)" : "var(--c-border,#1a3550)"}`, background: form.role === "agency_admin" ? "rgba(251,191,36,.08)" : "transparent", color: form.role === "agency_admin" ? "#fbbf24" : "var(--c-text3,#45688a)", fontSize: ".72rem", fontWeight: 700, cursor: "pointer", fontFamily: "inherit", textAlign: "center" }}>
                    <div>🔧 Admin</div>
                  </button>
                )}
              </div>
            </div>

            {/* Name */}
            <div style={{ marginBottom: ".5rem" }}>
              <label style={{ fontSize: ".62rem", color: "var(--c-text3,#45688a)", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", display: "block", marginBottom: 4 }}>{es ? "Nombre Completo" : "Full Name"}</label>
              <input className="dkt-inp" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="" style={{ width: "100%", boxSizing: "border-box" }} />
            </div>

            {/* Email */}
            <div style={{ marginBottom: ".5rem" }}>
              <label style={{ fontSize: ".62rem", color: "var(--c-text3,#45688a)", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", display: "block", marginBottom: 4 }}>Email</label>
              <input className="dkt-inp" type="email" value={form.email || ""} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="" style={{ width: "100%", boxSizing: "border-box" }} />
            </div>

            {/* Assign to agent (only for assistants, only if agency owner) */}
            {form.role === "assistant" && isAgencyOwner && (
              <div style={{ marginBottom: ".5rem" }}>
                <label style={{ fontSize: ".62rem", color: "var(--c-text3,#45688a)", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", display: "block", marginBottom: 4 }}>{es ? "Asignar a Agente" : "Assign to Agent"}</label>
                <select className="dkt-inp" value={form.parentAgentId || ""} onChange={e => setForm(f => ({ ...f, parentAgentId: e.target.value }))} style={{ width: "100%" }}>
                  {agents.map(a => <option key={a.id} value={a.id}>{a.name} ({nombreDelRol(a, users, lang).texto})</option>)}
                </select>
              </div>
            )}

            {/* PIN */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: ".5rem", marginBottom: ".5rem" }}>
              <div>
                <label style={{ fontSize: ".62rem", color: "var(--c-text3,#45688a)", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", display: "block", marginBottom: 4 }}>PIN</label>
                <input className="dkt-inp" type="text" inputMode="numeric" autoComplete="off" data-1p-ignore data-lpignore="true" maxLength={4} value={form.pin} onChange={e => { setForm(f => ({ ...f, pin: e.target.value.replace(/\D/, "") })); setPinErr(""); }} placeholder="••••" style={{ width: "100%", boxSizing: "border-box", WebkitTextSecurity: "disc" }} />
              </div>
              <div>
                <label style={{ fontSize: ".62rem", color: "var(--c-text3,#45688a)", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", display: "block", marginBottom: 4 }}>{es ? "Confirmar PIN" : "Confirm PIN"}</label>
                <input className="dkt-inp" type="text" inputMode="numeric" autoComplete="off" data-1p-ignore data-lpignore="true" maxLength={4} value={confirmPin} onChange={e => { setConfirmPin(e.target.value.replace(/\D/, "")); setPinErr(""); }} placeholder="••••" style={{ width: "100%", boxSizing: "border-box", WebkitTextSecurity: "disc" }} />
              </div>
            </div>
            {pinErr && <div style={{ fontSize: ".7rem", color: "#ef4444", marginBottom: ".5rem" }}>{pinErr}</div>}

            {/* Color */}
            <div style={{ marginBottom: "1rem" }}>
              <label style={{ fontSize: ".62rem", color: "var(--c-text3,#45688a)", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", display: "block", marginBottom: 4 }}>Color</label>
              <div style={{ display: "flex", gap: ".3rem" }}>
                {COLORS.map(c => (
                  <button key={c} type="button" onClick={() => setForm(f => ({ ...f, color: c }))}
                    style={{ width: 28, height: 28, borderRadius: "50%", backgroundColor: c, border: form.color === c ? "2px solid #fff" : "2px solid transparent", cursor: "pointer", transform: form.color === c ? "scale(1.15)" : "scale(1)" }} />
                ))}
              </div>
            </div>

            <button type="button" onClick={save} disabled={!form.name.trim()}
              style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 5, background: !form.name.trim() ? "#2a4560" : "linear-gradient(135deg,#059669,#10b981)", border: "none", color: "#fff", fontSize: ".82rem", fontWeight: 700, padding: ".55rem", borderRadius: 9, cursor: !form.name.trim() ? "not-allowed" : "pointer", fontFamily: "inherit" }}>
              <UserPlus size={14} />{es ? "Agregar" : "Add"} {roleLabels[form.role]}
            </button>
          </div>
        </div>
      )}

      {/* EDIT USER MODAL */}
      {modal && modal !== "add" && modal !== "invite" && (
        <div style={{ position: "fixed", inset: 0, zIndex: 9999, background: "rgba(0,0,0,.85)", display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" }}>
          <div style={{ background: "var(--c-surface,#0d1c2e)", border: "1px solid var(--c-border,#1a3550)", borderRadius: 14, padding: "1.5rem", maxWidth: 440, width: "100%" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
              <div style={{ fontWeight: 800, fontSize: "1rem", color: "var(--c-white,#eef6ff)" }}>{es ? "Editar Usuario" : "Edit User"}</div>
              <button type="button" onClick={() => setModal(null)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--c-text3,#45688a)" }}><X size={18} /></button>
            </div>
            <div style={{ marginBottom: ".5rem" }}>
              <label style={{ fontSize: ".62rem", color: "var(--c-text3,#45688a)", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", display: "block", marginBottom: 4 }}>{es ? "Nombre" : "Name"}</label>
              <input className="dkt-inp" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} style={{ width: "100%", boxSizing: "border-box" }} />
            </div>
            <div style={{ marginBottom: ".5rem" }}>
              <label style={{ fontSize: ".62rem", color: "var(--c-text3,#45688a)", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", display: "block", marginBottom: 4 }}>Email</label>
              <input className="dkt-inp" type="email" value={form.email || ""} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} style={{ width: "100%", boxSizing: "border-box" }} />
            </div>
            {form.role === "assistant" && isAgencyOwner && (
              <div style={{ marginBottom: ".5rem" }}>
                <label style={{ fontSize: ".62rem", color: "var(--c-text3,#45688a)", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", display: "block", marginBottom: 4 }}>{es ? "Asignar a Agente" : "Assign to Agent"}</label>
                <select className="dkt-inp" value={form.parentAgentId || ""} onChange={e => setForm(f => ({ ...f, parentAgentId: e.target.value }))} style={{ width: "100%" }}>
                  {agents.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </div>
            )}
            {form.role === "assistant" && (
              <div style={{ marginBottom: ".5rem" }}>
                <label style={{ fontSize: ".62rem", color: "var(--c-text3,#45688a)", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", display: "block", marginBottom: 4 }}>{es ? "Etiqueta del asiento (opcional)" : "Seat label (optional)"}</label>
                <input className="dkt-inp" type="text" maxLength={80} value={form.seatLabel || ""} onChange={e => setForm(f => ({ ...f, seatLabel: e.target.value }))} placeholder={es ? "ej. María — Recepción" : "e.g. María — Front Desk"} style={{ width: "100%", boxSizing: "border-box" }} />
                <div style={{ fontSize: ".58rem", color: "var(--c-text3,#45688a)", marginTop: 3, lineHeight: 1.4 }}>
                  {es ? "Cómo aparece este asiento en tu equipo. Si lo dejas vacío, usa el nombre real." : "How this seat appears in your team. If left blank, the real name is used."}
                </div>
              </div>
            )}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: ".5rem", marginBottom: ".5rem" }}>
              <div>
                <label style={{ fontSize: ".62rem", color: "var(--c-text3,#45688a)", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", display: "block", marginBottom: 4 }}>PIN</label>
                <input className="dkt-inp" type="text" inputMode="numeric" autoComplete="off" data-1p-ignore data-lpignore="true" maxLength={4} value={form.pin} onChange={e => { setForm(f => ({ ...f, pin: e.target.value.replace(/\D/, "") })); setPinErr(""); }} placeholder="••••" style={{ width: "100%", boxSizing: "border-box", WebkitTextSecurity: "disc" }} />
              </div>
              <div>
                <label style={{ fontSize: ".62rem", color: "var(--c-text3,#45688a)", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", display: "block", marginBottom: 4 }}>{es ? "Confirmar PIN" : "Confirm PIN"}</label>
                <input className="dkt-inp" type="text" inputMode="numeric" autoComplete="off" data-1p-ignore data-lpignore="true" maxLength={4} value={confirmPin} onChange={e => { setConfirmPin(e.target.value.replace(/\D/, "")); setPinErr(""); }} placeholder="••••" style={{ width: "100%", boxSizing: "border-box", WebkitTextSecurity: "disc" }} />
              </div>
            </div>
            {pinErr && <div style={{ fontSize: ".7rem", color: "#ef4444", marginBottom: ".5rem" }}>{pinErr}</div>}
            <div style={{ marginBottom: "1rem" }}>
              <label style={{ fontSize: ".62rem", color: "var(--c-text3,#45688a)", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", display: "block", marginBottom: 4 }}>Color</label>
              <div style={{ display: "flex", gap: ".3rem" }}>
                {COLORS.map(c => (
                  <button key={c} type="button" onClick={() => setForm(f => ({ ...f, color: c }))}
                    style={{ width: 28, height: 28, borderRadius: "50%", backgroundColor: c, border: form.color === c ? "2px solid #fff" : "2px solid transparent", cursor: "pointer", transform: form.color === c ? "scale(1.15)" : "scale(1)" }} />
                ))}
              </div>
            </div>
            <button type="button" onClick={save} disabled={!form.name.trim()}
              style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 5, background: !form.name.trim() ? "#2a4560" : "linear-gradient(135deg,#0099cc,#0055aa)", border: "none", color: "#fff", fontSize: ".82rem", fontWeight: 700, padding: ".55rem", borderRadius: 9, cursor: !form.name.trim() ? "not-allowed" : "pointer", fontFamily: "inherit" }}>
              <Save size={14} />{es ? "Guardar Cambios" : "Save Changes"}
            </button>
          </div>
        </div>
      )}

      {/* INVITE MODAL */}
      {modal === "invite" && (
        <div style={{ position: "fixed", inset: 0, zIndex: 9999, background: "rgba(0,0,0,.85)", display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" }}>
          <div style={{ background: "var(--c-surface,#0d1c2e)", border: "1px solid var(--c-border,#1a3550)", borderRadius: 14, padding: "1.5rem", maxWidth: 460, width: "100%" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
              <div style={{ fontWeight: 800, fontSize: "1rem", color: "var(--c-white,#eef6ff)" }}>{es ? "Invitar por Email" : "Invite by Email"}</div>
              <button type="button" onClick={() => setModal(null)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--c-text3,#45688a)" }}><X size={18} /></button>
            </div>

            {!inviteSent ? (
              <>
                <div style={{ marginBottom: ".65rem" }}>
                  <label style={{ fontSize: ".62rem", color: "var(--c-text3,#45688a)", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", display: "block", marginBottom: 4 }}>{es ? "Email del invitado" : "Invitee's Email"}</label>
                  <input className="dkt-inp" type="email" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} placeholder="" style={{ width: "100%", boxSizing: "border-box" }} />
                </div>

                <div style={{ marginBottom: ".65rem" }}>
                  <label style={{ fontSize: ".62rem", color: "var(--c-text3,#45688a)", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", display: "block", marginBottom: 4 }}>{es ? "Invitar como" : "Invite as"}</label>
                  <div style={{ display: "flex", gap: ".3rem" }}>
                    {canAddAgent && (
                      <button type="button" onClick={() => setInviteRole("agent")}
                        style={{ flex: 1, padding: ".4rem", borderRadius: 8, border: `1px solid ${inviteRole === "agent" ? "rgba(0,255,136,.4)" : "var(--c-border,#1a3550)"}`, background: inviteRole === "agent" ? "rgba(0,255,136,.08)" : "transparent", color: inviteRole === "agent" ? "var(--c-green,#00ff88)" : "var(--c-text3,#45688a)", fontSize: ".72rem", fontWeight: 700, cursor: "pointer", fontFamily: "inherit", textAlign: "center" }}>
                        👤 {es ? "Agente" : "Agent"}
                      </button>
                    )}
                    {isSolo && !soloAdminSlotTaken && (
                      <button type="button" onClick={() => setInviteRole("agent")}
                        style={{ flex: 1, padding: ".4rem", borderRadius: 8, border: `1px solid ${inviteRole === "agent" ? "rgba(251,191,36,.4)" : "var(--c-border,#1a3550)"}`, background: inviteRole === "agent" ? "rgba(251,191,36,.08)" : "transparent", color: inviteRole === "agent" ? "#fbbf24" : "var(--c-text3,#45688a)", fontSize: ".72rem", fontWeight: 700, cursor: "pointer", fontFamily: "inherit", textAlign: "center" }}>
                        👑 Admin
                      </button>
                    )}
                    <button type="button" onClick={() => setInviteRole("assistant")}
                      style={{ flex: 1, padding: ".4rem", borderRadius: 8, border: `1px solid ${inviteRole === "assistant" ? "rgba(129,140,248,.4)" : "var(--c-border,#1a3550)"}`, background: inviteRole === "assistant" ? "rgba(129,140,248,.08)" : "transparent", color: inviteRole === "assistant" ? "#818cf8" : "var(--c-text3,#45688a)", fontSize: ".72rem", fontWeight: 700, cursor: "pointer", fontFamily: "inherit", textAlign: "center" }}>
                      💺 {es ? "Asiento" : "Seat"}
                    </button>
                  </div>
                  {isSolo && inviteRole === "agent" && (
                    <div style={{ marginTop: ".45rem", fontSize: ".62rem", color: "var(--c-text2,#7aa3c0)", lineHeight: 1.45, background: "rgba(251,191,36,.06)", border: "1px solid rgba(251,191,36,.25)", borderRadius: 8, padding: ".45rem .6rem" }}>
                      👑 <b style={{ color: "#fbbf24" }}>{es ? `Admin — acceso total (${adminPriceLine}).` : `Admin — full access (${adminPriceLine}).`}</b> {es ? "Queda admin automáticamente en cuanto acepte la invitación — verá y manejará todo tu CRM. Solo puedes tener 1 admin. No se cobra durante la beta." : "They become a full-access admin automatically as soon as they accept — they'll see and manage your entire CRM. You can have only 1 admin. Not billed during the beta."}
                    </div>
                  )}
                </div>

                {inviteRole === "assistant" && isAgencyOwner && (
                  <div style={{ marginBottom: ".65rem" }}>
                    <label style={{ fontSize: ".62rem", color: "var(--c-text3,#45688a)", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", display: "block", marginBottom: 4 }}>{es ? "Asignar a Agente" : "Assign to Agent"}</label>
                    <select className="dkt-inp" value={inviteParent} onChange={e => setInviteParent(e.target.value)} style={{ width: "100%" }}>
                      {agents.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                    </select>
                  </div>
                )}

                {/* Email preview */}
                <div style={{ marginBottom: ".75rem" }}>
                  <div style={{ fontSize: ".6rem", color: "var(--c-text3,#45688a)", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", marginBottom: ".3rem" }}>{es ? "Vista previa del email" : "Email Preview"}</div>
                  <div style={{ background: "#ffffff", borderRadius: 10, overflow: "hidden", border: "1px solid #e0e0e0" }}>
                    <div style={{ background: "#f8f9fa", padding: ".4rem .65rem", borderBottom: "1px solid #e0e0e0" }}>
                      <div style={{ fontSize: ".6rem", color: "#666" }}><b style={{ color: "#333" }}>From:</b> {currentUser?.name} via Deskte</div>
                      <div style={{ fontSize: ".6rem", color: "#666" }}><b style={{ color: "#333" }}>To:</b> {inviteEmail || "colleague@email.com"}</div>
                      <div style={{ fontSize: ".6rem", color: "#666" }}><b style={{ color: "#333" }}>Subject:</b> {es ? `${currentUser?.name} te invita a unirte a Deskte` : `${currentUser?.name} invited you to join Deskte`}</div>
                    </div>
                    <div style={{ padding: ".65rem", fontSize: ".7rem", color: "#333", lineHeight: 1.7 }}>
                      <div style={{ textAlign: "center", marginBottom: ".5rem" }}>
                        <span style={{ fontSize: "1rem", fontWeight: 800, color: "#0d1c2e" }}>DESKTE</span>
                      </div>
                      <p style={{ margin: "0 0 .4rem" }}>{es ? `Hola,` : `Hi there,`}</p>
                      <p style={{ margin: "0 0 .4rem" }}>{es
                        ? `${currentUser?.name} te ha invitado a unirte a su equipo en Deskte.`
                        : `${currentUser?.name} has invited you to join their team on Deskte.`}</p>
                      <div style={{ textAlign: "center", margin: ".6rem 0" }}>
                        <span style={{ display: "inline-block", background: "linear-gradient(135deg,#059669,#10b981)", color: "#fff", padding: ".4rem 1.2rem", borderRadius: 8, fontWeight: 700, fontSize: ".75rem" }}>
                          {es ? "Aceptar Invitación" : "Accept Invitation"}
                        </span>
                      </div>
                      <p style={{ margin: 0, fontSize: ".55rem", color: "#999", textAlign: "center" }}>Powered by Deskte — Insurance Agent CRM</p>
                    </div>
                  </div>
                </div>

                {/* SE ACABARON LOS ASIENTOS. Un aviso rojo con el limite y punto
                    muerto seria dejarla plantada; esto dice el precio y la saca
                    del paso con un clic. Es la otra mitad del tope: bloquear
                    sin ensennar la salida no es cobrar, es estorbar.

                    DICE DOS COSAS DISTINTAS, y la diferencia la manda el
                    servidor en `enPrueba` (Lissette, 2026-09-22):
                      - con plan activo -> se cobra ahora, prorrateado.
                      - en prueba       -> se agrega y se le cobra al activar
                                           el plan, porque el checkout cuenta
                                           los asientos que ya tiene.
                    Prometer un cobro inmediato a quien no tiene tarjeta puesta
                    seria mentirle, y callar que se pagara despues, tambien.

                    Y EL TITULO CAMBIA CUANDO EL TOPE ES CERO: ningun plan
                    incluye asientos, asi que a casi todo el mundo esto le sale
                    en el PRIMERO. "Ya usaste todos tus asientos (0)" leido por
                    quien no ha usado ninguno no se entiende. */}
                {faltaAsiento && (
                  <div style={{ background: "rgba(255,183,0,.08)", border: "1px solid rgba(255,183,0,.35)", borderRadius: 10, padding: ".7rem .8rem", marginBottom: ".6rem" }}>
                    <div style={{ fontWeight: 700, fontSize: ".8rem", color: "var(--c-white,#eef6ff)", marginBottom: ".25rem" }}>
                      💺 {faltaAsiento.limite === 0
                        ? (es ? "Tu plan no incluye asientos" : "Your plan includes no seats")
                        : (es ? "Ya usaste todos tus asientos" : "You've used all your seats")}
                      {faltaAsiento.limite != null && faltaAsiento.limite > 0 && (
                        <span style={{ fontWeight: 500, color: "var(--c-text2,#7aa3c0)" }}> ({faltaAsiento.limite})</span>
                      )}
                    </div>
                    <p style={{ fontSize: ".72rem", color: "var(--c-text2,#7aa3c0)", margin: "0 0 .6rem", lineHeight: 1.45 }}>
                      {faltaAsiento.enPrueba
                        ? (es
                          ? "Agrega uno por $9.99/mes. Durante tu prueba no se te cobra nada: se suma a tu plan el dia que lo actives."
                          : "Add one for $9.99/mo. Nothing is charged during your trial: it joins your plan the day you activate it.")
                        : (es
                          ? "Agrega uno por $9.99/mes. Se cobra ahora la parte que queda de este mes y se suma a tu recibo."
                          : "Add one for $9.99/mo. The rest of this month is charged now and it joins your invoice.")}
                    </p>
                    <div style={{ display: "flex", gap: ".4rem", flexWrap: "wrap" }}>
                      <button type="button" onClick={comprarYReintentar} disabled={comprando}
                        style={{ display: "inline-flex", alignItems: "center", gap: 5, background: comprando ? "#2a4560" : "linear-gradient(135deg,#f59e0b,#d97706)", border: "none", color: "#fff", fontSize: ".75rem", fontWeight: 800, padding: ".45rem .9rem", borderRadius: 8, cursor: comprando ? "wait" : "pointer", fontFamily: "inherit" }}>
                        {comprando
                          ? (faltaAsiento.enPrueba
                            ? (es ? "Agregando..." : "Adding...")
                            : (es ? "Cobrando..." : "Charging..."))
                          : (es ? "Agregar asiento y enviar" : "Add seat and send")}
                      </button>
                      <button type="button" onClick={() => setFaltaAsiento(null)}
                        style={{ background: "transparent", border: "1px solid var(--c-border,#1a3550)", color: "var(--c-text2,#7aa3c0)", fontSize: ".75rem", fontWeight: 600, padding: ".45rem .9rem", borderRadius: 8, cursor: "pointer", fontFamily: "inherit" }}>
                        {es ? "Ahora no" : "Not now"}
                      </button>
                    </div>
                  </div>
                )}

                <button type="button" onClick={sendInvite} disabled={!inviteEmail.trim() || inviteBusy}
                  style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 5, background: (!inviteEmail.trim() || inviteBusy) ? "#2a4560" : "linear-gradient(135deg,#0099cc,#0055aa)", border: "none", color: "#fff", fontSize: ".82rem", fontWeight: 700, padding: ".55rem", borderRadius: 9, cursor: (!inviteEmail.trim() || inviteBusy) ? "not-allowed" : "pointer", fontFamily: "inherit" }}>
                  <Send size={14} />{inviteBusy ? (es ? "Enviando..." : "Sending...") : (es ? "Enviar Invitación" : "Send Invitation")}
                </button>
              </>
            ) : (
              <div style={{ textAlign: "center", padding: ".5rem" }}>
                <div style={{ fontSize: "2.5rem", marginBottom: ".5rem" }}>✅</div>
                <div style={{ fontWeight: 800, fontSize: "1rem", color: "var(--c-white,#eef6ff)", marginBottom: ".3rem" }}>{es ? "Invitación Creada" : "Invitation Created"}</div>
                <p style={{ fontSize: ".78rem", color: "var(--c-text3,#45688a)", marginBottom: ".75rem" }}>
                  {es ? `Se envió la invitación a ` : `Invitation sent to `}<b style={{ color: "var(--c-cyan,#00d4ff)" }}>{inviteSent.email}</b>
                </p>
                {isApiModeUM && inviteSent.emailSent === false && (
                  <div style={{ background: "rgba(255,183,0,.08)", border: "1px solid rgba(255,183,0,.3)", borderRadius: 8, padding: ".5rem .65rem", marginBottom: ".5rem", fontSize: ".68rem", color: "var(--c-amber,#ffb700)", textAlign: "left" }}>
                    ⚠️ {es ? "El email NO se envió (SMTP no configurado). Copia el link abajo y compártelo manualmente por ahora." : "Email NOT sent (SMTP not configured). Copy the link below and share manually for now."}
                  </div>
                )}
                <div style={{ background: "var(--c-surface2,#112538)", border: "1px solid var(--c-border,#1a3550)", borderRadius: 8, padding: ".5rem .65rem", marginBottom: ".75rem" }}>
                  <div style={{ fontSize: ".58rem", color: "var(--c-text3,#45688a)", marginBottom: 3 }}>{es ? "Link de invitación (expira en 7 días):" : "Invitation link (expires in 7 days):"}</div>
                  <div style={{ fontSize: ".65rem", color: "var(--c-cyan,#00d4ff)", wordBreak: "break-all" }}>{inviteSent.link}</div>
                </div>
                <button type="button" onClick={() => { navigator.clipboard.writeText(inviteSent.link).catch(() => {}); toast.success(es ? "¡Copiado!" : "Copied!"); }}
                  style={{ display: "inline-flex", alignItems: "center", gap: 5, background: "var(--c-surface2,#112538)", border: "1px solid var(--c-border,#1a3550)", color: "var(--c-cyan,#00d4ff)", fontSize: ".75rem", fontWeight: 700, padding: ".4rem .85rem", borderRadius: 8, cursor: "pointer", fontFamily: "inherit", marginRight: ".4rem" }}>
                  <Copy size={13} />{es ? "Copiar Link" : "Copy Link"}
                </button>
                <button type="button" onClick={() => setModal(null)}
                  style={{ display: "inline-flex", alignItems: "center", gap: 5, background: "linear-gradient(135deg,#059669,#10b981)", border: "none", color: "#fff", fontSize: ".75rem", fontWeight: 700, padding: ".4rem .85rem", borderRadius: 8, cursor: "pointer", fontFamily: "inherit" }}>
                  {es ? "Listo" : "Done"}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* DELETE CONFIRM */}
      {delConfirm && (
        <div style={{ position: "fixed", inset: 0, zIndex: 9999, background: "rgba(0,0,0,.85)", display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" }}>
          <div style={{ background: "var(--c-surface,#0d1c2e)", border: "1px solid var(--c-border,#1a3550)", borderRadius: 14, padding: "1.5rem", maxWidth: 380, width: "100%", textAlign: "center" }}>
            <div style={{ fontSize: "2rem", marginBottom: ".5rem" }}>⚠️</div>
            <div style={{ fontWeight: 800, fontSize: ".95rem", color: "var(--c-white,#eef6ff)", marginBottom: ".5rem" }}>{es ? "¿Eliminar este usuario?" : "Remove this user?"}</div>
            <p style={{ fontSize: ".75rem", color: "var(--c-text3,#45688a)", marginBottom: "1rem" }}>{es ? "Esta acción no se puede deshacer." : "This action cannot be undone."}</p>
            <div style={{ display: "flex", gap: ".5rem" }}>
              <button type="button" onClick={() => setDelConfirm(null)} style={{ flex: 1, background: "var(--c-surface2,#112538)", border: "1px solid var(--c-border,#1a3550)", color: "var(--c-text,#c8dff0)", fontSize: ".8rem", fontWeight: 700, padding: ".5rem", borderRadius: 9, cursor: "pointer", fontFamily: "inherit" }}>{es ? "Cancelar" : "Cancel"}</button>
              <button type="button" onClick={async () => {
                const id = delConfirm;
                // Optimistic remove from UI.
                onUsersChange(prev => prev.filter(u => u.id !== id));
                setDelConfirm(null);
                if (isApiModeUM) {
                  try {
                    await apiDeleteTeamMember(id);
                  } catch (err) {
                    // Roll back optimistic remove + show error.
                    toast.error((es ? "No se pudo eliminar: " : "Could not remove: ") + err.message);
                    // Best-effort: refetch happens elsewhere. We don't have the original
                    // user object handy to re-insert, so the user sees the error and can
                    // refresh to get the canonical list.
                  }
                }
              }} style={{ flex: 1, background: "linear-gradient(135deg,#dc2626,#ef4444)", border: "none", color: "#fff", fontSize: ".8rem", fontWeight: 700, padding: ".5rem", borderRadius: 9, cursor: "pointer", fontFamily: "inherit" }}>{es ? "Eliminar" : "Remove"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// La tarjeta de cada miembro del equipo.
//
// VIVE AQUI FUERA, A NIVEL DE MODULO, A PROPOSITO. Volver a meterla dentro
// de UsersManager reproduce exactamente el fallo que reporto Lissette el
// 2026-09-02 (queja 3): "cuando se marca un permiso se va al principio de la
// pagina a cada rato".
//
// Por que. Definida dentro del cuerpo del padre, esta funcion era un objeto
// NUEVO en cada render. React compara los componentes por identidad de tipo,
// asi que marcar un permiso -> cambia el estado del padre -> render -> tipo
// distinto -> React DESMONTA la tarjeta entera y la vuelve a montar. Al
// reemplazarse los nodos del navegador, la pagina se queda sin altura un
// instante y el scroll cae al principio; de paso la casilla pierde el foco.
// Nunca fue un fallo de scroll: era un remontaje en cada clic.
//
// Fuera, el tipo es estable, React actualiza en el sitio y el scroll se queda
// donde estaba. No usa ningun hook, asi que no habia nada mas que mover.
function UserCard({ u, indent, ctx }) {
  const {
    users, onUsersChange, currentUser, lang, es, isAgencyOwner, isAgent,
   isSolo, roleIcons, openEdit, setDelConfirm, expandedPerms,
   setExpandedPerms, roleConfirm, setRoleConfirm, booksPrivate, agents,
   soloActiveAdmin, adminPriceLine,
  } = ctx;
    const isMe = u.id === currentUser?.id;
    const assistantCount = users.filter(a => a.role === "assistant" && a.parentAgentId === u.id).length;
    const parentAgent = u.parentAgentId ? users.find(a => a.id === u.parentAgentId) : null;
    const canEdit = isAgencyOwner || (isAgent && u.parentAgentId === currentUser?.id) || isMe;
    const canDelete = (isAgencyOwner && u.id !== currentUser?.id) || (isAgent && u.role === "assistant" && u.parentAgentId === currentUser?.id) || (isSolo && !isMe && (u.role === "agency_owner" || u.role === "agent"));

    // Co-owner / admin promotion. An AGENT can be promoted to a co-owner
    // (agency_owner) — full access to the whole CRM. Agency owners do this for
    // co-owners; a SOLO agent does it for their single add-on admin, capped at
    // one (once an admin exists the option disappears). Never self — the
    // backend also refuses to strand a multi-owner agency without an owner.
    const isSoloAdminCard = isSolo; // label + price differ for the solo add-on
    const canMakeCoOwner = (isAgencyOwner || (isSolo && !soloActiveAdmin)) && u.role === "agent" && !isMe;
    const canRemoveCoOwner = (isAgencyOwner || isSolo) && u.role === "agency_owner" && !isMe;

    // Permission management: only the assistant's direct supervisor (or agency owner) can grant.
    const isMySupervisee = u.role === "assistant" && u.parentAgentId === currentUser?.id;
    const canManagePerms = isAgencyOwner || isMySupervisee;
    // AMPLIAR EL ALCANCE de un asiento (verlo todo, o la cartera de los
    // duennos) es de quien manda en la cuenta. En un plan Solo NO HAY DUENNA:
    // manda la titular, `solo_agent`. El desplegable se le pintaba apagado
    // —"Solo la dueña lo cambia"— aunque el servidor si se lo permite
    // (`mandaEnLaAgencia`, team.routes). Un control apagado que el servidor
    // habria aceptado es una mentira, igual que uno encendido que rechaza.
    const puedeAmpliarAlcance = isAgencyOwner || isSolo;
    const showPermsSection = canManagePerms && u.role === "assistant" && !isMe;

    // Orphaned assistant (no supervisor) → any agent in the agency can claim them.
    const isOrphan = u.role === "assistant" && !u.parentAgentId && !isMe;
    const canClaimAsSupervisor = isOrphan && (isAgencyOwner || isAgent);

    // Granular per-module permission toggle (writes to seatPermissions JSON).
    const seatPermsObj = normalizeSeatPermissions(u.seatPermissions);
    const toggleSeatPerm = async (key, value) => {
      // Optimistic update.
      const next = { ...seatPermsObj, [key]: value };
      if (typeof onUsersChange === "function") {
        onUsersChange(prev => prev.map(x => x.id === u.id ? { ...x, seatPermissions: next } : x));
      }
      try {
        await apiUpdateSeatProfile(u.id, { seatPermissions: { [key]: value } });
      } catch (err) {
        // Roll back on failure.
        if (typeof onUsersChange === "function") {
          onUsersChange(prev => prev.map(x => x.id === u.id ? { ...x, seatPermissions: seatPermsObj } : x));
        }
        toast.error((es ? "Error: " : "Error: ") + err.message);
      }
    };

    // Bulk: turn every action in a group on or off in one call.
    const setGroupPerms = async (group, value) => {
      const patch = {};
      const next = { ...seatPermsObj };
      group.actions.forEach((a) => {
        if (a.legacy) return; // legacy mailbox flags handled separately
        patch[a.key] = value;
        next[a.key] = value;
      });
      if (Object.keys(patch).length === 0) return;
      if (typeof onUsersChange === "function") {
        onUsersChange(prev => prev.map(x => x.id === u.id ? { ...x, seatPermissions: next } : x));
      }
      try {
        await apiUpdateSeatProfile(u.id, { seatPermissions: patch });
      } catch (err) {
        if (typeof onUsersChange === "function") {
          onUsersChange(prev => prev.map(x => x.id === u.id ? { ...x, seatPermissions: seatPermsObj } : x));
        }
        toast.error((es ? "Error: " : "Error: ") + err.message);
      }
    };

    const expandedSet = new Set(expandedPerms[u.id] || []);
    const toggleGroupExpanded = (groupId) => {
      setExpandedPerms(prev => {
        const cur = new Set(prev[u.id] || []);
        if (cur.has(groupId)) cur.delete(groupId); else cur.add(groupId);
        return { ...prev, [u.id]: Array.from(cur) };
      });
    };

    const claimSupervisor = async () => {
      try {
        await apiUpdateAssistantSupervisor(u.id, currentUser.id);
        if (typeof onUsersChange === "function") {
          onUsersChange(prev => prev.map(x => x.id === u.id ? { ...x, parentAgentId: currentUser.id, parentAgentName: currentUser.name } : x));
        }
      } catch (err) {
        console.error("[supervisor] claim failed:", err);
        toast.error((es ? "Error: " : "Error: ") + err.message);
      }
    };

    // Promote AGENT -> co-owner (agency_owner) or set a co-owner back to AGENT.
    // toBackendRole is the backend enum string ("AGENCY_OWNER" | "AGENT").
    const doChangeRole = async (toBackendRole) => {
      const toFrontRole = toBackendRole === "AGENCY_OWNER" ? "agency_owner" : "agent";
      const prevRole = u.role;
      // Optimistic: owners have no supervisor, so clear parentAgentId on promote.
      if (typeof onUsersChange === "function") {
        onUsersChange(prev => prev.map(x => x.id === u.id
          ? { ...x, role: toFrontRole, parentAgentId: toFrontRole === "agency_owner" ? null : x.parentAgentId }
          : x));
      }
      try {
        await apiUpdateMemberRole(u.id, toBackendRole);
        toast.success(es ? "Rol actualizado" : "Role updated");
      } catch (err) {
        if (typeof onUsersChange === "function") {
          onUsersChange(prev => prev.map(x => x.id === u.id ? { ...x, role: prevRole } : x));
        }
        toast.error((es ? "Error: " : "Error: ") + err.message);
      }
      setRoleConfirm(null);
    };

    // Alcance de datos del asiento:
    //   "supervisor" → solo la cartera de su agente (lo de siempre).
    //   "owners"     → la cartera de los dueños (agencias de libros privados).
    //   "agency"     → TODA la agencia. Lissette 2026-08-29: "en una agencia
    //                  con dos agentes los seat tienen que ver sus clientes de
    //                  ambas". Se enciende asiento por asiento a propósito.
    //   "propios"    → SOLO los prospectos que ese asiento cree. Lissette
    //                  2026-09-20, asistentes contratados solo para llamar una
    //                  lista: "lo unico que quiero es que esas personas puedan
    //                  ver ese cliente que creen como prospecto, no mas
    //                  modulos". Es el unico que ESTRECHA.
    const changeSeatScope = async (scope) => {
      const next = (scope === "owners" || scope === "agency" || scope === SOLO_SUS_PROSPECTOS) ? scope : null;
      const prev = u.seatScope || null;
      if (typeof onUsersChange === "function") {
        onUsersChange(p => p.map(x => x.id === u.id ? { ...x, seatScope: next } : x));
      }
      try {
        await apiUpdateSeatProfile(u.id, { seatScope: next || "supervisor" });
      } catch (err) {
        if (typeof onUsersChange === "function") {
          onUsersChange(p => p.map(x => x.id === u.id ? { ...x, seatScope: prev } : x));
        }
        toast.error((es ? "Error: " : "Error: ") + err.message);
      }
    };

    return (
      <div key={u.id} style={{ display: "flex", flexDirection: "column", gap: ".5rem", background: "var(--c-surface2,#112538)", border: `1px solid ${isMe ? "rgba(0,212,255,.25)" : "var(--c-border,#1a3550)"}`, borderRadius: 12, padding: ".65rem .85rem", marginLeft: indent ? "1.8rem" : 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: ".65rem" }}>
          <div style={{ width: 40, height: 40, borderRadius: "50%", background: u.color || "#1d6fa4", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: ".78rem", color: "#fff", flexShrink: 0 }}>{u.initials}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: ".35rem", flexWrap: "wrap" }}>
              <span style={{ fontWeight: 700, fontSize: ".82rem", color: "var(--c-white,#eef6ff)" }}>{u.name}</span>
              {isMe && <span style={{ fontSize: ".55rem", padding: "1px 6px", borderRadius: 100, background: "rgba(0,212,255,.12)", color: "var(--c-cyan,#00d4ff)", fontWeight: 700 }}>{es ? "Tú" : "You"}</span>}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: ".4rem", marginTop: 2, flexWrap: "wrap" }}>
              <span style={{ fontSize: ".68rem", color: "var(--c-text3,#45688a)" }}>{u.role === "assistant" && u.seatLabel ? `${roleIcons[u.role]} ${u.seatLabel}` : nombreDelRol(u, users, lang).etiqueta}</span>
              {u.email && <span style={{ fontSize: ".62rem", color: "var(--c-text3,#45688a)", opacity: .7 }}>· {u.email}</span>}
            </div>
            {["agent","solo_agent"].includes(u.role) && assistantCount > 0 && (
              <div style={{ fontSize: ".62rem", color: "var(--c-cyan,#00d4ff)", marginTop: 3 }}>💺 {assistantCount} {es ? (assistantCount === 1 ? "asiento" : "asientos") : (assistantCount === 1 ? "seat" : "seats")}</div>
            )}
            {u.role === "assistant" && parentAgent && (
              <div style={{ fontSize: ".62rem", color: "var(--c-text3,#45688a)", marginTop: 3 }}>{es ? "Asignado a:" : "Assigned to:"} <span style={{ color: "var(--c-white,#eef6ff)", fontWeight: 600 }}>{parentAgent.name}</span></div>
            )}
          </div>
          <div style={{ display: "flex", gap: ".25rem" }}>
            {canMakeCoOwner && <button type="button" onClick={() => setRoleConfirm({ id: u.id, to: "AGENCY_OWNER" })} title={isSoloAdminCard ? (es ? `Hacer Admin — acceso total (${adminPriceLine})` : `Make Admin — full access (${adminPriceLine})`) : (es ? "Hacer admin de agencia" : "Make agency admin")} style={{ background: "var(--c-surface3,#162d44)", border: "1px solid var(--c-border,#1a3550)", color: "var(--c-amber,#ffb700)", cursor: "pointer", padding: "5px 8px", borderRadius: 7, fontSize: ".62rem", fontWeight: 700, lineHeight: 1 }}>👑 {isSoloAdminCard ? (es ? "Hacer Admin" : "Make Admin") : (es ? "Admin de agencia" : "Agency admin")}</button>}
            {canRemoveCoOwner && <button type="button" onClick={() => setRoleConfirm({ id: u.id, to: "AGENT" })} title={isSoloAdminCard ? (es ? "Quitar Admin (volver a agente)" : "Remove admin (back to agent)") : (es ? "Quitar admin de agencia (volver a agente)" : "Remove agency admin (back to agent)")} style={{ background: "var(--c-surface3,#162d44)", border: "1px solid var(--c-border,#1a3550)", color: "var(--c-text3,#45688a)", cursor: "pointer", padding: "5px 8px", borderRadius: 7, fontSize: ".62rem", fontWeight: 700, lineHeight: 1 }}>↓ {es ? "A agente" : "To agent"}</button>}
            {canEdit && <button type="button" onClick={() => openEdit(u)} style={{ background: "var(--c-surface3,#162d44)", border: "1px solid var(--c-border,#1a3550)", color: "var(--c-text3,#45688a)", cursor: "pointer", padding: "5px 7px", borderRadius: 7, lineHeight: 0 }}><Edit2 size={13} /></button>}
            {canDelete && <button type="button" onClick={() => setDelConfirm(u.id)} style={{ background: "var(--c-surface3,#162d44)", border: "1px solid var(--c-border,#1a3550)", color: "#ef4444", cursor: "pointer", padding: "5px 7px", borderRadius: 7, lineHeight: 0 }}><Trash2 size={13} /></button>}
          </div>
        </div>

        {roleConfirm?.id === u.id && (
          <div style={{ background: "rgba(255,184,0,.08)", border: "1px solid rgba(255,184,0,.3)", borderRadius: 8, padding: ".55rem .7rem", display: "flex", alignItems: "center", gap: ".65rem", flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 200, fontSize: ".7rem", color: "var(--c-white,#eef6ff)", lineHeight: 1.35 }}>
              {roleConfirm.to === "AGENCY_OWNER"
                ? (isSoloAdminCard
                    ? (es ? `¿Hacer a ${u.name} tu Admin con acceso total? Verá y manejará TODO tu CRM (clientes, pólizas, reportes). Costo: ${adminPriceLine} — sin cobro durante la beta.` : `Make ${u.name} your full-access Admin? They will see and manage your ENTIRE CRM (clients, policies, reports). Price: ${adminPriceLine} — not billed during the beta.`)
                    : (es ? `¿Hacer a ${u.name} admin de agencia? Verá y manejará TODO el CRM de esta cuenta de agencia.` : `Make ${u.name} an agency admin? They will see and manage this agency account's entire CRM.`))
                : (isSoloAdminCard
                    ? (es ? `¿Quitar a ${u.name} como Admin y dejarlo como agente? Dejará de tener acceso total.` : `Remove ${u.name} as Admin and set them back to agent? They will lose full access.`)
                    : (es ? `¿Quitar a ${u.name} como admin de agencia y dejarlo como agente? Volverá a ver solo su propia cartera.` : `Set ${u.name} back to agent? They will only see their own clients again.`))}
            </div>
            <div style={{ display: "flex", gap: ".4rem" }}>
              <button type="button" onClick={() => doChangeRole(roleConfirm.to)} style={{ background: "var(--c-amber,#ffb700)", border: "none", color: "#1a1200", cursor: "pointer", padding: "5px 11px", borderRadius: 7, fontSize: ".66rem", fontWeight: 800 }}>{es ? "Sí, confirmar" : "Yes, confirm"}</button>
              <button type="button" onClick={() => setRoleConfirm(null)} style={{ background: "var(--c-surface3,#162d44)", border: "1px solid var(--c-border,#1a3550)", color: "var(--c-text3,#45688a)", cursor: "pointer", padding: "5px 11px", borderRadius: 7, fontSize: ".66rem", fontWeight: 700 }}>{es ? "Cancelar" : "Cancel"}</button>
            </div>
          </div>
        )}

        {canClaimAsSupervisor && (
          <div style={{ background: "rgba(255,184,0,.08)", border: "1px solid rgba(255,184,0,.3)", borderRadius: 8, padding: ".55rem .7rem", display: "flex", alignItems: "center", gap: ".65rem", flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ fontSize: ".7rem", fontWeight: 700, color: "var(--c-amber,#ffb700)", marginBottom: 2 }}>
                ⚠️ {es ? "Asiento sin supervisor" : "Seat without supervisor"}
              </div>
              <div style={{ fontSize: ".62rem", color: "var(--c-text2,#7aa3c0)", lineHeight: 1.4 }}>
                {es ? "Asígnate como supervisor para gestionar sus permisos." : "Claim as supervisor to manage their permissions."}
              </div>
            </div>
            <button type="button" onClick={claimSupervisor} style={{ background: "linear-gradient(135deg,#f59e0b,#d97706)", border: "none", color: "#fff", fontSize: ".7rem", fontWeight: 700, padding: ".4rem .85rem", borderRadius: 7, cursor: "pointer" }}>
              {es ? "Asignarme" : "Assign me"}
            </button>
          </div>
        )}

        {/* ALCANCE DEL ASISTENTE.
            EN UNA CUENTA DE DOS AGENTES NO SE ENSEÑA EL SELECTOR, y no es por
            simplificar: ahí NO HARÍA NADA. El servidor aplica la regla
            automática antes de mirar este ajuste, así que un asiento de esa
            cuenta ve todo aunque aquí pusiera "solo su supervisor". Un control
            que dice que restringe y no restringe miente, y este llegó a estar
            así unas horas el 2026-08-29 (Lissette lo vio: *"esto es necesario
            tenerlo ahora con ese cambio?"*). En su lugar se explica POR QUÉ.

            En una agencia de verdad sí hace falta: allí los agentes tienen
            carteras separadas y este selector es la única forma de ampliar
            un asiento. Las opciones anchas solo se las ofrece a la dueña, y el
            servidor las rechaza igual si alguien lo intenta por otro camino. */}
        {(isAgencyOwner || isMySupervisee) && u.role === "assistant" && !isMe && (
          esCuentaDeDosAgentes(users) ? (
            /* Cuenta de dos agentes: ampliar no tiene sentido (ya lo ven todo),
               pero ESTRECHAR sí. Sin esta casilla, el alcance "solo sus
               prospectos" existiría para todas las agencias menos estas, y de
               la única forma mala: sin decirlo en ninguna parte. */
            <div style={{ background: "rgba(0,212,255,.06)", border: "1px solid rgba(0,212,255,.25)", borderRadius: 8, padding: ".5rem .7rem", fontSize: ".68rem", color: "var(--c-text2,#7aa3c0)", display: "flex", flexDirection: "column", gap: ".4rem" }}>
              <span>
                {es
                  ? "Ve toda la cuenta: los dos agentes comparten la misma cartera."
                  : "Sees the whole account: both agents share the same book of business."}
              </span>
              <label style={{ display: "flex", alignItems: "center", gap: ".4rem", cursor: puedeAmpliarAlcance ? "pointer" : "not-allowed", opacity: puedeAmpliarAlcance ? 1 : .6 }}>
                <input type="checkbox" disabled={!puedeAmpliarAlcance} checked={esSoloSusProspectos(u.seatScope)}
                  onChange={(e) => changeSeatScope(e.target.checked ? SOLO_SUS_PROSPECTOS : "supervisor")} />
                <span>{es ? "Salvo que solo vea los prospectos que ella cree" : "Unless they only see the prospects they create"}</span>
              </label>
            </div>
          ) : (
          <div style={{ background: "rgba(0,212,255,.06)", border: "1px solid rgba(0,212,255,.25)", borderRadius: 8, padding: ".5rem .7rem", display: "flex", alignItems: "center", gap: ".6rem", flexWrap: "wrap" }}>
            <span style={{ fontSize: ".68rem", fontWeight: 700, color: "var(--c-white,#eef6ff)" }}>{es ? "Alcance del asistente:" : "Assistant scope:"}</span>
            <select value={valorDelDesplegable(u.seatScope)} onChange={(e) => changeSeatScope(e.target.value)} disabled={!puedeAmpliarAlcance} title={!puedeAmpliarAlcance ? (es ? "Solo quien manda en la cuenta puede cambiarlo" : "Only the account owner can change this") : undefined} style={{ background: "var(--c-surface3,#162d44)", border: "1px solid var(--c-border,#1a3550)", color: "var(--c-white,#eef6ff)", fontSize: ".68rem", fontWeight: 600, padding: ".3rem .5rem", borderRadius: 7, cursor: puedeAmpliarAlcance ? "pointer" : "not-allowed", opacity: puedeAmpliarAlcance ? 1 : .6, fontFamily: "inherit" }}>
              <option value="supervisor">{es ? "Solo su supervisor" : "Their supervisor only"}</option>
              {booksPrivate && <option value="owners">{es ? "Cartera de los dueños" : "Owners\u2019 shared pool"}</option>}
              <option value="agency">{es ? "Toda la agencia" : "The whole agency"}</option>
              <option value={SOLO_SUS_PROSPECTOS}>{es ? "Solo los prospectos que cree" : "Only the prospects they create"}</option>
            </select>
            {!puedeAmpliarAlcance && (
              <span style={{ fontSize: ".62rem", color: "var(--c-text3,#45688a)" }}>
                {es ? "Solo quien manda en la cuenta lo cambia" : "Only the account owner changes this"}
              </span>
            )}
            {u.seatScope === "agency" && (
              <span style={{ fontSize: ".62rem", color: "var(--c-text2,#7aa3c0)" }}>
                {es
                  ? "Ve los clientes de todos los agentes de la agencia."
                  : "Sees every agent\u2019s clients in the agency."}
              </span>
            )}
            {esSoloSusProspectos(u.seatScope) && (
              <span style={{ fontSize: ".62rem", color: "var(--c-text2,#7aa3c0)" }}>
                {es
                  ? "Solo ve las fichas que cree ella, y solo mientras sigan en Prospecto. Los clientes que ya existen no los ve nunca, ni buscando. El estado lo pasa a Activo otra persona."
                  : "Sees only the records they create, and only while those stay in Prospect. Existing clients never show up, not even by search. Someone else moves them to Active."}
              </span>
            )}
          </div>
          )
        )}

        {showPermsSection && (
          <div style={{ background: "var(--c-surface,#0d1c2e)", border: "1px solid var(--c-border,#1a3550)", borderRadius: 8, padding: ".55rem .7rem" }}>
            <div style={{ display: "flex", alignItems: "center", gap: ".4rem", marginBottom: ".5rem" }}>
              <Shield size={12} style={{ color: "var(--c-cyan,#00d4ff)" }} />
              <div style={{ fontSize: ".62rem", color: "var(--c-cyan,#00d4ff)", textTransform: "uppercase", letterSpacing: ".5px", fontWeight: 700 }}>
                {es ? "Permisos del asiento" : "Seat permissions"}
              </div>
            </div>
            <div style={{ fontSize: ".58rem", color: "var(--c-text3,#45688a)", marginBottom: ".5rem", lineHeight: 1.5 }}>
              {es
                ? "Tú decides exactamente qué puede ver y hacer este asiento en tu cuenta. Por seguridad (HIPAA), todo arranca apagado."
                : "You decide exactly what this seat can see and do in your account. For HIPAA safety, everything starts off."}
            </div>

            {SEAT_PERMISSION_GROUPS.map((group) => {
              const isExpanded = expandedSet.has(group.id);
              const flagsForGroup = group.actions.map((a) => seatPermsObj[a.key] === true);
              const onCount = flagsForGroup.filter(Boolean).length;
              const totalCount = group.actions.length;
              const allOn = onCount === totalCount;
              const anyOn = onCount > 0;
              return (
                <div key={group.id} style={{ borderTop: "1px solid var(--c-border,#1a3550)", paddingTop: ".4rem", marginTop: ".4rem" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: ".4rem" }}>
                    <button type="button" onClick={() => toggleGroupExpanded(group.id)} style={{ flex: 1, display: "flex", alignItems: "center", gap: ".4rem", background: "transparent", border: "none", color: "var(--c-text,#c8dff0)", cursor: "pointer", padding: ".15rem 0", textAlign: "left", fontFamily: "inherit" }}>
                      {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                      <span style={{ fontSize: ".72rem", fontWeight: 700 }}>{es ? group.label.es : group.label.en}</span>
                      <span style={{ fontSize: ".58rem", color: anyOn ? "var(--c-cyan,#00d4ff)" : "var(--c-text3,#45688a)", fontWeight: 600 }}>
                        {/* En los ramos, NINGUNO marcado significa "los ve
                            todos", no "ninguno". Un "(0/18)" ahi se leeria
                            justo al reves de lo que hace. */}
                        {group.id === "policyTypes" && !anyOn
                          ? (es ? "(todos)" : "(all)")
                          : `(${onCount}/${totalCount})`}
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setGroupPerms(group, !allOn)}
                      title={allOn ? (es ? "Quitar todos" : "Turn all off") : (es ? "Activar todos" : "Turn all on")}
                      style={{ background: "var(--c-surface2,#112538)", border: "1px solid var(--c-border,#1a3550)", color: allOn ? "#ef4444" : "var(--c-cyan,#00d4ff)", fontSize: ".58rem", fontWeight: 700, padding: "2px 8px", borderRadius: 6, cursor: "pointer", fontFamily: "inherit" }}
                    >
                      {allOn ? (es ? "Quitar" : "Off") : (es ? "Todos" : "All")}
                    </button>
                  </div>
                  {isExpanded && (
                    <div style={{ paddingLeft: "1.1rem", marginTop: ".25rem" }}>
                      {group.note && (
                        <div style={{ fontSize: ".62rem", color: "var(--c-text3,#45688a)", lineHeight: 1.4, marginBottom: ".35rem" }}>
                          {es ? group.note.es : group.note.en}
                        </div>
                      )}
                      {group.actions.map((a, i) => {
                        const checked = seatPermsObj[a.key] === true;
                        return (
                          <label key={a.key} style={{ display: "flex", alignItems: "center", gap: ".5rem", cursor: "pointer", padding: ".22rem 0" }}>
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(e) => toggleSeatPerm(a.key, e.target.checked)}
                              style={{ accentColor: "var(--c-cyan,#00d4ff)" }}
                            />
                            <span style={{ fontSize: ".68rem", color: "var(--c-text,#c8dff0)" }}>{es ? a.label.es : a.label.en}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
}
